import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ClipboardList, Check, X, Phone, Mail } from "lucide-react";

export default function TdApplications() {
  const [apps, setApps] = useState([]);

  const load = async () => {
    const { data } = await api.get("/admin/td/applications");
    setApps(data);
  };
  useEffect(() => { load(); }, []);

  const approve = async (a) => {
    try {
      await api.post(`/admin/td/applications/${a.id}/approve`);
      toast.success(`${a.name} is now a Tournament Director`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
  };

  const reject = async (a) => {
    if (!window.confirm(`Reject and delete ${a.name}'s director application?`)) return;
    await api.delete(`/admin/td/applications/${a.id}`);
    toast.success("Application removed");
    load();
  };

  const pending = apps.filter((a) => a.status === "pending");
  if (apps.length === 0) return null;

  return (
    <div className="mt-8 bg-pb-surface border border-pb-border p-5" data-testid="td-applications">
      <div className="flex items-center gap-2 mb-1">
        <ClipboardList className="w-5 h-5 text-pb-red" />
        <span className="font-display font-bold text-lg uppercase">Tournament Director Applications</span>
        {pending.length > 0 && <span className="bg-pb-red text-black text-xs font-bold px-2 py-0.5" data-testid="td-pending-badge">{pending.length} pending</span>}
      </div>
      <p className="text-sm text-zinc-500 mb-4">Approved directors can create and run tournaments (brackets, scores, finals).</p>
      <div className="space-y-3">
        {apps.map((a) => (
          <div key={a.id} data-testid={`td-app-${a.id}`} className="border border-pb-border bg-pb-elevated p-4 flex flex-wrap items-start justify-between gap-3">
            <div className="text-sm space-y-1">
              <div className="font-display font-bold uppercase flex items-center gap-2">
                {a.name}
                {a.status !== "pending" && <span className="text-[9px] uppercase tracking-widest text-zinc-500 border border-pb-border px-1.5">{a.status}</span>}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-400">
                <span className="flex items-center gap-1"><Phone className="w-3 h-3 text-pb-cyan" />{a.phone}</span>
                <span className="flex items-center gap-1"><Mail className="w-3 h-3 text-pb-cyan" />{a.email}</span>
              </div>
              {a.experience && <div className="text-xs text-zinc-500">Experience: {a.experience}</div>}
              {a.message && <div className="text-xs text-zinc-400 italic">"{a.message}"</div>}
            </div>
            {a.status === "approved" && (
              <div className="flex gap-2 shrink-0">
                <button data-testid={`grant-demo-lab-${a.id}`}
                  onClick={async () => {
                    try {
                      const { data } = await api.post("/admin/td/demo-lab", { email: a.email, approved: true });
                      toast.success(`${data.name} can now use the Tournament Demo Lab`);
                    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                  }}
                  className="flex items-center gap-1.5 border border-pb-cyan/60 text-pb-cyan font-bold uppercase tracking-widest text-[10px] px-3 py-1.5 hover:bg-pb-cyan hover:text-black transition-colors">
                  <Check className="w-3 h-3" /> Grant Demo Lab
                </button>
                <button data-testid={`revoke-demo-lab-${a.id}`}
                  onClick={async () => {
                    try {
                      const { data } = await api.post("/admin/td/demo-lab", { email: a.email, approved: false });
                      toast.success(`Demo Lab access revoked for ${data.name}`);
                    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                  }}
                  className="flex items-center gap-1.5 border border-pb-border text-zinc-500 font-bold uppercase tracking-widest text-[10px] px-3 py-1.5 hover:border-red-500 hover:text-red-400 transition-colors">
                  <X className="w-3 h-3" /> Revoke
                </button>
              </div>
            )}
            {a.status === "pending" && (
              <div className="flex gap-2 shrink-0">
                <button data-testid={`approve-td-${a.id}`} onClick={() => approve(a)}
                  className="flex items-center gap-1.5 bg-pb-red text-black font-bold uppercase tracking-widest text-xs px-4 py-2 hover:bg-[#BEF264] active:scale-95 transition-all">
                  <Check className="w-3.5 h-3.5" /> Approve
                </button>
                <button data-testid={`reject-td-${a.id}`} onClick={() => reject(a)}
                  className="flex items-center gap-1.5 border border-pb-border text-zinc-400 font-bold uppercase tracking-widest text-xs px-4 py-2 hover:border-red-500 hover:text-red-400 transition-colors">
                  <X className="w-3.5 h-3.5" /> Reject
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
